import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import styled from 'styled-components/macro';
import DOMPurify from 'dompurify';

import { IconVolumeOn /* IconOpenDictionary */ } from '@mirinae/react-ui';
import { os } from '@mirinae/js-utils/utils/userAgent';
import { classes } from '@mirinae/react-ui';
import GTM from '@mirinae/apis/GoogleAnalytics';

import Element from '@mirinae/lessons/components/lesson/elements/Element';
import { playAzureTTS, playTTS } from '@mirinae/lessons/modules/libs/lessonLib';
import { getMetricsForStrings } from '@mirinae/lessons/modules/libs/layoutLib';
import { lessonsSizes, fontDefault, lessonsPalette, zIndex } from '@mirinae/lessons/modules/defines/styles';
import { fontDefaultAlt } from '@mirinae/react-ui';

import { LessonButton } from '@mirinae/lessons/modules/defines/styles';
import animateIcon from '@mirinae/lessons/assets/images/animate.svg';
import animateHoverIcon from '@mirinae/lessons/assets/images/animate-hover.svg';
import http, { defaultOptions } from '@mirinae/lessons/modules/apis/http';
import httpAPI from '@mirinae/lessons/modules/apis/http';
import { apiPaths } from '@mirinae/defines/paths';
import axios from 'axios';
import Localization from '@mirinae/shared/il8n';

const LetterCardElement = styled(Element)`
    display: flex;
    flex-direction: row;
    justify-content: center;;
    ${!os.isOthers && 'min-width: 100%;'}
`;

const CardWrapper = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    position: relative;
    height: ${os.isOthers ? '100%' : 'calc(100% - 8px)'};
    width: ${props => (os.isOthers ? '100%' : `${props.geom.width}px`)};
    ${props => os.isOthers && `min-width: ${props.geom.width}px`};
    ${!os.isOthers && 'max-width: calc(100vw - 100px);'} // room for the side stpe-controls in mobile on narrow phones 
`;

const Card = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: calc(100% - 49px);
    background:  ${lessonsPalette.primary.white};
    border: 1px solid ${lessonsPalette.grey[6]};
    box-sizing: border-box;
    box-shadow: ${props => (props.playing ? 'none' : '4px 4px 13px rgba(0, 0, 0, 0.07)')};
    border-radius: 10px;
    cursor: pointer;
    z-index: ${zIndex.float};
    transition: all 350ms linear;

    &:hover {
        box-shadow: ${props => (props.playing ? 'none' : '4px 4px 13px 0px rgba(0, 0, 0, 0.20)')};
    }
`;

const Pronunciation = styled.div`
    position: absolute;
    bottom: 75px;
    color: #6E9292;
    text-align: center;
    font-family: ${fontDefault};
    font-size: 16px;
    font-weight: 500;
`;

const ButtonStyle = `
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    top: 16px;
    width: 35px;
    height: 35px;
`;
const PlayAudioButton = styled(LessonButton)`
    left: 15px;
    ${ButtonStyle}
`;

const AnimateButton = styled.button`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 5px;
    border: none;
    margin-top: 24px;
    width: fit-content;
    justify-content: center;
    color: #48A9A6;
    font-family: ${fontDefaultAlt};
    font-size: 12px;
    font-style: normal;
    font-weight: 700;
    line-height: 15px; /* 125% */
    padding: 5px 18px;
    border-radius: 15.5px;
    background: #E0FBFA;
  
    img {
        content: url(${animateIcon});
    }
  
    &:hover {
        background: #48A9A6;
        color: white;
        img {
            content: url(${animateHoverIcon});
        }
    }
  
    .${classes.mobile} & {
        margin-top: 35px;
    }

`;

const AnimateIcon = styled.img`
`;

const EmbeddedSVG = styled.div`
    position: absolute;
    top: ${props => props.top}px;
`;

const EmbeddedImg = styled.img`
    position: absolute;
    top: ${props => props.top}px;
`;

const Graphic = ({ graphic, scale, embedRef }) => {
    const [cleanSVG, setCleanSVG] = useState('');
    const gotSVG = useRef(false);
    const scaleVal = scale ? Number.parseFloat(scale.value) : 1.0;
    const [top, setTop] = useState(15);

    useEffect(() => {
        if (graphic.mimeType === 'image/svg+xml' && embedRef.current && !gotSVG.current) {
            gotSVG.current = true;
            axios(graphic.url, {
                method: 'GET',
                withCredentials: false,
                headers: { 'Content-Type': 'image/svg+xml', },
            }).then(response => {
                // console.log(response);
                setCleanSVG(DOMPurify.sanitize(response.data, { ADD_TAGS: ['script'] }));
            })
            .catch(error => {
                console.log('error');
            });
        }
    }, []);

    useLayoutEffect(() => {
        if (embedRef.current && embedRef.current.clientHeight) {
            setTop(34 + 0); // (embedRef.current ? embedRef.current.clientHeight * (1 - scaleVal) / 2 : 0));
        }
    }, [cleanSVG]);

    // it appears the script contained in the retrieved SVG is not automatically running, pull it out here and run it by hand.
    useEffect(() => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(cleanSVG, 'image/svg+xml');
        const scriptElement = doc.querySelector('script');

        if (scriptElement) {
            const script = document.createElement('script');
            script.innerHTML = scriptElement.textContent;
            document.body.appendChild(script);
            return () => {
                document.body.removeChild(script);
            };
        }
    }, [cleanSVG]);

    return (
        graphic.mimeType === 'image/svg+xml' ? (
            <EmbeddedSVG ref={embedRef} top={top} dangerouslySetInnerHTML={{__html: cleanSVG}} style={{ transform: `scale(${scaleVal})` }} />
        ) : (
            <EmbeddedImg ref={embedRef} top={top} src={graphic.url} style={{ transform: `scale(${scaleVal})` }} />
        )
    );
};

export const voicings = {
    'Male slow': { speaker: 'male', speed: 5, pitch: 1 },
    'Male normal': { speaker: 'male', speed: 2, pitch: 1 },
    'Male fast': { speaker: 'male', speed: -2, pitch: 1 },
    'Female slow': { speaker: 'female', speed: 5, pitch: 1 },
    'Female normal': { speaker: 'female', speed: 2, pitch: 1 },
    'Female fast': { speaker: 'female', speed: -2, pitch: 1 },
};

export const azureVoicings = {
    'Male slow': { speaker: 'GookMin', speed: '0.75', pitch: '100%' },
    'Male normal': { speaker: 'GookMin', speed: '1.0', pitch: '100%' },
    'Male fast': { speaker: 'GookMin', speed: '1.25', pitch: '100%' },
    'Female slow': { speaker: 'SoonBok', speed: '0.75', pitch: '100%' },
    'Female normal': { speaker: 'SoonBok', speed: '1.0', pitch: '100%' },
    'Female fast': { speaker: 'SoonBok', speed: '1.25', pitch: '100%' },
};

const LetterCard = ({ element, geomElement }) => {
    const [state, setState] = useState('closed');
    const [audioEl, setAudioEl] = useState(null);
    const [playing, setPlaying] = useState(false);
    const embedRef = useRef(null);

    const animate = () => {
        const player = embedRef.current.querySelector('svg').svgatorPlayer;
        if (player) {
            if (player.totalTime) {
                player.play();
            } else {
                player.ready(p => p.play());
            }
            GTM.userAction('letter-animation', { letter: element.fieldsByName.letter.value }, 'lessons');
        }
    };

    const playAudio = (e) => {
        setPlaying(true);
        // playTTS(audioEl, element.fieldsByName.letter.value, e, voicings[element.fieldsByName?.voicing?.value || 'Male slow']);
        playAzureTTS(audioEl, element.fieldsByName.letter.value, e, azureVoicings[element.fieldsByName?.voicing?.value || 'Female slow']);

        GTM.userAction('letter-audio', { word: element.fieldsByName.letter.value }, 'lessons');
    };

    return (
        <LetterCardElement className={`el-${element.componentName}`} element={element} geomElement={geomElement}>
            <CardWrapper geom={os.isOthers ? geomElement.geom : (geomElement.mobile && geomElement.mobile.geom)}>
                <Card className="letter-card"  onClick={playAudio} playing={playing}>
                    <PlayAudioButton type="button"><IconVolumeOn color={playing ? '#48A9A6' : '#525252'}/></PlayAudioButton>
                    { element.fieldsByName.graphic.url &&
                        <Graphic graphic={element.fieldsByName.graphic} embedRef={embedRef} scale={element.fieldsByName.scale} /> }
                    <Pronunciation>{element.fieldsByName.pronunciation.value}</Pronunciation>
                </Card>
                <AnimateButton onClick={animate} playing={playing}><AnimateIcon alt="Play animation"/></AnimateButton>
            </CardWrapper>
            <audio
                ref={setAudioEl}
                onEnded={() => setPlaying(false)}
            />
        </LetterCardElement>
    );
};

export default LetterCard;
